import {startsWith} from 'ramda';

import {SALES_PERSON_RESULT_URL_PATH} from '../constants/quickSearch';

import {
    QueryItemType,
    ProductItemType,
    ResQueryItemType,
    ResProductItemType,
    AlgoliaAutocompleteProductType,
    AlgoliaAutocompleteSuggestionType,
    AlgoliaAutocompleteDTOType,
    QuickSearchResultsType,
} from '../types';

/**
 * Nornmalizes Algolia link
 * @param {ProductItemType} product - product got with Algolia direct call / backend call
 * @returns {ResProductItemType} - product with normalized link
 */
export const normalizeAlgoliaLinks = (product: ProductItemType): ResProductItemType => ({
    link: product.url.replace(/.+\.(com)/, ''),
    ...product,
});

/**
 * Normalizes Algolia image link
 * @param {string} link - initial link with glasses.usa domain
 * @param {string} - link without domain
 * @returns {string} - normalized link
 */
export const normalizeAlgoliaImageLink = (link: string): string => link.replace(/.+\.(com)/, '');

/**
 * Normalizes product got from direct Algolia call
 * @param {AlgoliaAutocompleteProductType} product - product got with Algolia direct call
 * @returns {ProductItemType} - normalized product
 */
export const normalaizeAlgoliaProductData = ({
    title,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    image_link,
    sku,
    link,
}: AlgoliaAutocompleteProductType): ResProductItemType => ({
    name: title,
    image: normalizeAlgoliaImageLink(image_link),
    sku,
    link: startsWith('/', link) ? link : `/${link}`,
});

/**
 * Normalizes suggestion got from direct Algolia call
 * @param {AlgoliaAutocompleteSuggestionType} suggestion - suggestion got with Algolia direct call
 * @returns {ResQueryItemType} - normalized suggestion
 */
export const normalizeAlgoliaSuggestionData = ({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    display_value,
    link,
}: AlgoliaAutocompleteSuggestionType): ResQueryItemType => ({
    name: display_value,
    link,
});

/**
 * Normalizes data got with Algolia direct call
 * @param {AlgoliaAutocompleteResponseType} data - response from Algolia
 * @returns {QuickSearchResultsType} - normalized autocomplete data
 */
export const normalizeDirectAlgoliaResponse = (
    data: AlgoliaAutocompleteDTOType,
): QuickSearchResultsType => {
    const suggestionsData = data.results[0].hits;
    const productsData = data.results[1].hits;
    const productsList = productsData.map(product => normalaizeAlgoliaProductData(product));
    const queriesList = suggestionsData.map(suggestion =>
        normalizeAlgoliaSuggestionData(suggestion),
    );

    return {
        queriesList,
        productsList,
    };
};

/**
 *  Get new queries quick search results
 *  @param { Array<QueryItemType>} queries - response
 *  @return {QuickSearchResultsType} - Array with results
 */
export const getNewQueries = (queries: Array<QueryItemType>): ResQueryItemType[] =>
    queries.map(elm => {
        return elm.url
            ? {link: elm.url.replace(/.+\.(com)/, ''), ...elm}
            : {link: `/${SALES_PERSON_RESULT_URL_PATH}/?q=${elm.name}`, ...elm};
    });
